import React, { useState, useRef, useEffect, useCallback } from "react";
import { Mic, Square, Volume2, VolumeX, Send, WifiOff, Loader2, Sparkles, RotateCcw } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { getVoiceName, getOrCreateSessionId, getReadingMode, getWakeWord, getWakeTokens, getWakePhrase, getSpeechPace, shouldShowBargeHint, noteBargeHintShown } from "../lib/kioskConfig";
import { matchOffline } from "../data/offlineFaq";
import { detectEmotion } from "../data/emotionKeywords";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Reading-mode caption panel — highlights words in sync with audio playback
function ReadingCaption({ text, audioEl, active }) {
  const [progress, setProgress] = useState(0);
  const scrollRef = useRef(null);
  const activeRef = useRef(null);

  useEffect(() => {
    if (!audioEl || !active) return;
    let raf;
    const tick = () => {
      const dur = audioEl.duration || 0;
      const cur = audioEl.currentTime || 0;
      setProgress(dur > 0 ? Math.min(1, cur / dur) : 0);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [audioEl, active]);

  useEffect(() => {
    if (activeRef.current && scrollRef.current) {
      const el = activeRef.current;
      const parent = scrollRef.current;
      const elTop = el.offsetTop - parent.offsetTop;
      parent.scrollTo({ top: Math.max(0, elTop - parent.clientHeight / 2 + el.clientHeight / 2), behavior: "smooth" });
    }
  }, [progress]);

  const words = text.split(/(\s+)/); // keep spaces
  const wordOnlyIndices = words.reduce((acc, w, i) => {
    if (w.trim().length) acc.push(i);
    return acc;
  }, []);
  const activeWordIdx = wordOnlyIndices[Math.min(wordOnlyIndices.length - 1, Math.floor(progress * wordOnlyIndices.length))];

  return (
    <div
      data-testid="reading-caption"
      ref={scrollRef}
      className="mt-6 max-h-64 overflow-y-auto rounded-3xl bg-white border-2 border-primary/30 shadow-inner px-6 py-5 leading-loose"
    >
      <div className="text-xs uppercase tracking-widest font-bold text-primary mb-2">
        Reading mode
      </div>
      <p className="text-2xl sm:text-3xl font-medium text-foreground/40 leading-relaxed">
        {words.map((w, i) => {
          if (!w.trim()) return <span key={i}>{w}</span>;
          const done = i < activeWordIdx;
          const isActive = i === activeWordIdx;
          return (
            <span
              key={i}
              ref={isActive ? activeRef : null}
              className={
                isActive
                  ? "text-primary font-bold bg-primary/10 rounded px-1 transition-colors"
                  : done
                  ? "text-foreground transition-colors"
                  : "transition-colors"
              }
            >
              {w}
            </span>
          );
        })}
      </p>
    </div>
  );
}

export default function Chat() {
  const { t, lang } = useLang();
  const [sessionId] = useState(() => getOrCreateSessionId());

  const [status, setStatus] = useState("idle"); // idle | recording | transcribing | thinking | speaking
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const [history, setHistory] = useState([]);
  const [muted, setMuted] = useState(false);
  const [typedInput, setTypedInput] = useState("");
  const [offline, setOffline] = useState(!navigator.onLine);
  const [error, setError] = useState("");
  const [micBlocked, setMicBlocked] = useState(false);
  const [readingMode, setReadingModeState] = useState(() => getReadingMode());
  const [currentAudio, setCurrentAudio] = useState(null);
  const [captionText, setCaptionText] = useState("");

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const liveRecogRef = useRef(null);
  const audioCtxRef = useRef(null);
  const vadRafRef = useRef(null);
  const audioQueueRef = useRef([]);
  const currentAudioRef = useRef(null);        // stable handle to the currently-playing chunk
  const stopSpeakingSignalRef = useRef(false);
  const wakeRecogRef = useRef(null);
  const bargeStreamRef = useRef(null);
  const bargeCtxRef = useRef(null);
  const bargeRafRef = useRef(null);
  const emotionSpeakerRef = useRef(null);

  const [wakeEnabled, setWakeEnabled] = useState(() => getWakeWord());
  const [wakePhrase, setWakePhrase] = useState(() => getWakePhrase());
  const [showBargeHint, setShowBargeHint] = useState(false);

  const STT_LOCALE = { en: "en-IN", hi: "hi-IN", mr: "mr-IN", te: "te-IN" };

  // Split text into ~180-char chunks at sentence boundaries so every chunk gets full TTS.
  const splitIntoSentences = (text, maxLen = 180) => {
    const parts = text.match(/[^.!?।॥\n]+[.!?।॥\n]*/g) || [text];
    const chunks = [];
    let cur = "";
    for (const p of parts) {
      if ((cur + p).length > maxLen && cur.length > 0) {
        chunks.push(cur.trim()); cur = p;
      } else {
        cur += p;
      }
    }
    if (cur.trim()) chunks.push(cur.trim());
    return chunks.filter(Boolean);
  };

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    const refreshReading = () => setReadingModeState(getReadingMode());
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("sakhi:photo-updated", refreshReading);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("sakhi:photo-updated", refreshReading);
    };
  }, []);

  const stopAudioPlayback = useCallback(() => {
    stopSpeakingSignalRef.current = true;
    audioQueueRef.current.forEach((a) => { try { a.pause(); a.src = ""; } catch { /* ignore */ } });
    audioQueueRef.current = [];
    if (currentAudioRef.current) {
      try { currentAudioRef.current.pause(); } catch { /* ignore */ }
      currentAudioRef.current = null;
    }
    setCurrentAudio(null);
  }, []);  // stable — no deps so nothing triggers cleanup between chunks

  // Play sentence-chunked TTS sequentially so the WHOLE reply is spoken.
  const playTTS = useCallback(async (text, replyLang) => {
    if (!text || muted) return;
    const ttsLang = (replyLang && replyLang !== "auto") ? replyLang : (lang !== "auto" ? lang : "hi");
    setCaptionText(text);
    setStatus("speaking");
    stopSpeakingSignalRef.current = false;

    const chunks = splitIntoSentences(text);
    for (let i = 0; i < chunks.length; i++) {
      if (stopSpeakingSignalRef.current) break;
      const chunk = chunks[i];
      try {
        const res = await fetch(`${BACKEND_URL}/api/voice/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: chunk,
            language: ttsLang,
            speaker: emotionSpeakerRef.current || getVoiceName() || "priya",
            pace: getSpeechPace(),
          }),
        });
        if (!res.ok) continue;
        const data = await res.json();
        if (stopSpeakingSignalRef.current) break;
        await new Promise((resolve) => {
          const audio = new Audio(`data:audio/wav;base64,${data.audio_base64}`);
          audioQueueRef.current.push(audio);
          currentAudioRef.current = audio;
          setCurrentAudio(audio);
          audio.onended = () => {
            audioQueueRef.current = audioQueueRef.current.filter((a) => a !== audio);
            if (currentAudioRef.current === audio) currentAudioRef.current = null;
            resolve();
          };
          audio.onerror = () => resolve();
          audio.play().catch(() => resolve());
        });
      } catch { /* skip broken chunk */ }
    }
    if (!stopSpeakingSignalRef.current) setStatus("idle");
    setCurrentAudio(null);
  }, [BACKEND_URL, lang, muted]);

  const handleOfflineFallback = useCallback((text) => {
    const answer = matchOffline(text) || t("offline_fallback");
    setReply(answer);
    setHistory((h) => [...h, { role: "user", text }, { role: "assistant", text: answer }]);
    playTTS(answer);
  }, [playTTS, t]);

  // Small helper to play a plain text via Sarvam TTS (used by the Say-it-again replay button)
  const speakTextOnce = useCallback(async (text) => {
    if (!text || muted) return;
    stopSpeakingSignalRef.current = false;
    setStatus("speaking");
    setCaptionText(text);
    const chunks = splitIntoSentences(text);
    const speaker = emotionSpeakerRef.current || getVoiceName() || "priya";
    const pace = getSpeechPace();
    const ttsLang = lang !== "auto" ? lang : "hi";
    for (const chunk of chunks) {
      if (stopSpeakingSignalRef.current) break;
      try {
        const res = await fetch(`${BACKEND_URL}/api/voice/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: chunk, language: ttsLang, speaker, pace }),
        });
        if (!res.ok) continue;
        const data = await res.json();
        if (stopSpeakingSignalRef.current) break;
        await new Promise((resolve) => {
          const audio = new Audio(`data:audio/wav;base64,${data.audio_base64}`);
          audioQueueRef.current.push(audio);
          currentAudioRef.current = audio;
          setCurrentAudio(audio);
          audio.onended = () => {
            audioQueueRef.current = audioQueueRef.current.filter((a) => a !== audio);
            if (currentAudioRef.current === audio) currentAudioRef.current = null;
            resolve();
          };
          audio.onerror = () => resolve();
          audio.play().catch(() => resolve());
        });
      } catch { /* skip */ }
    }
    if (!stopSpeakingSignalRef.current) setStatus("idle");
    setCurrentAudio(null);
  }, [BACKEND_URL, lang, muted]);

  const sendToBackend = useCallback(async (text, detectedLang) => {
    if (!text.trim()) return;
    emotionSpeakerRef.current = detectEmotion(text) === "soft" ? "pooja" : null;
    if (!navigator.onLine) { handleOfflineFallback(text); return; }

    setStatus("thinking");
    setReply("");
    setError("");
    setHistory((h) => [...h, { role: "user", text }]);
    stopSpeakingSignalRef.current = false;

    const chatLang = detectedLang || lang;
    const ttsLang = (detectedLang && detectedLang !== "auto") ? detectedLang : (lang !== "auto" ? lang : "hi");
    const speaker = emotionSpeakerRef.current || getVoiceName() || "priya";
    const pace = getSpeechPace();

    // ---- Interleaved streaming: as soon as a sentence lands from Gemini, kick off TTS
    // in parallel with the rest of the stream. Playback loop plays chunks in order.
    let accumulated = "";
    let processedLen = 0;
    let streamDone = false;
    let firstAudioReady = false;
    const audioPromises = [];  // ordered list of Promise<base64 audio>

    const fetchTTS = (chunk) =>
      fetch(`${BACKEND_URL}/api/voice/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: chunk, language: ttsLang, speaker, pace }),
      }).then((r) => (r.ok ? r.json() : null)).then((d) => d?.audio_base64 || null);

    const enqueueIfSentenceReady = (final = false) => {
      // Try to extract complete sentence(s) from accumulated
      // Sentence terminators: . ! ? । ॥ newline
      while (true) {
        const remaining = accumulated.slice(processedLen);
        if (!remaining.trim()) return;
        const m = remaining.match(/^([\s\S]*?[.!?।॥\n])(\s*)/);
        if (m && m[1].trim().length >= 3) {
          const sentence = m[1].trim();
          processedLen += m[0].length;
          if (!muted) audioPromises.push(fetchTTS(sentence));
        } else if (final) {
          // Flush leftover as one last chunk
          const leftover = remaining.trim();
          if (leftover.length >= 2 && !muted) audioPromises.push(fetchTTS(leftover));
          processedLen = accumulated.length;
          return;
        } else {
          return; // wait for more tokens
        }
      }
    };

    // Playback loop — plays audios in order as they become ready
    const playbackLoop = async () => {
      if (muted) return;
      let idx = 0;
      while (!stopSpeakingSignalRef.current) {
        if (idx < audioPromises.length) {
          const p = audioPromises[idx];
          idx++;
          let b64 = null;
          try { b64 = await p; } catch { /* ignore */ }
          if (!b64 || stopSpeakingSignalRef.current) continue;
          if (!firstAudioReady) {
            firstAudioReady = true;
            setStatus("speaking");
            setCaptionText(""); // reset caption; will grow with each chunk if reading mode is on
          }
          await new Promise((resolve) => {
            const audio = new Audio(`data:audio/wav;base64,${b64}`);
            audioQueueRef.current.push(audio);
            currentAudioRef.current = audio;
            setCurrentAudio(audio);
            audio.onended = () => {
              audioQueueRef.current = audioQueueRef.current.filter((a) => a !== audio);
              if (currentAudioRef.current === audio) currentAudioRef.current = null;
              resolve();
            };
            audio.onerror = () => resolve();
            audio.play().catch(() => resolve());
          });
        } else if (streamDone) {
          break; // nothing left to play
        } else {
          await new Promise((r) => setTimeout(r, 40));
        }
      }
    };

    // Kick off the playback loop concurrently
    const playbackP = playbackLoop();

    // Barge-in VAD: watch the mic (with echo cancellation) during speaking.
    // If the girl speaks for ≥400ms while Sakhi is playing, stop and start recording.
    let bargeStopped = false;
    const startBargeVAD = async () => {
      try {
        const bs = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        bargeStreamRef.current = bs;
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        bargeCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(bs);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.fftSize);
        const THRESHOLD = 18;      // higher than VAD auto-stop to ignore leakage
        const MIN_SPEECH_MS = 400;
        let firstLoudAt = 0;

        const tick = () => {
          if (bargeStopped || stopSpeakingSignalRef.current) return;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) { const d = buf[i] - 128; sum += d * d; }
          const rms = Math.sqrt(sum / buf.length);
          const now = performance.now();
          if (rms > THRESHOLD) {
            if (!firstLoudAt) firstLoudAt = now;
            else if (now - firstLoudAt > MIN_SPEECH_MS) {
              bargeStopped = true;
              stopAudioPlayback();
              try { bs.getTracks().forEach((tk) => tk.stop()); } catch { /* ignore */ }
              try { ctx.close(); } catch { /* ignore */ }
              bargeStreamRef.current = null;
              bargeCtxRef.current = null;
              setTimeout(() => startRecording(), 120);
              return;
            }
          } else {
            firstLoudAt = 0;
          }
          bargeRafRef.current = requestAnimationFrame(tick);
        };
        bargeRafRef.current = requestAnimationFrame(tick);
      } catch { /* mic denied or in use — skip barge-in silently */ }
    };
    // Start the barge-in listener slightly after playback begins so we don't false-trigger
    setTimeout(() => { if (!stopSpeakingSignalRef.current) startBargeVAD(); }, 900);

    let networkFailed = false;
    let finalReply = "";
    try {
      const res = await fetch(`${BACKEND_URL}/api/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, text, language: chatLang }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trimStart();
          if (payload === "[DONE]") continue;
          if (payload.startsWith("[ERROR]")) { networkFailed = true; continue; }
          accumulated += payload;
          setReply(accumulated);
          setCaptionText(accumulated); // reading mode caption grows live too
          enqueueIfSentenceReady(false);
        }
      }
    } catch {
      networkFailed = true;
    }

    // Flush leftover text as final TTS chunk
    enqueueIfSentenceReady(true);
    streamDone = true;
    finalReply = accumulated;

    if (networkFailed && !finalReply) {
      setHistory((h) => h.slice(0, -1));
      handleOfflineFallback(text);
      return;
    }

    setHistory((h) => [...h, { role: "assistant", text: finalReply }]);

    // Wait for playback to complete
    await playbackP;
    // Tear down barge-in resources
    bargeStopped = true;
    if (bargeRafRef.current) { cancelAnimationFrame(bargeRafRef.current); bargeRafRef.current = null; }
    if (bargeStreamRef.current) { try { bargeStreamRef.current.getTracks().forEach((tk) => tk.stop()); } catch { /* ignore */ } bargeStreamRef.current = null; }
    if (bargeCtxRef.current) { try { bargeCtxRef.current.close(); } catch { /* ignore */ } bargeCtxRef.current = null; }
    if (!stopSpeakingSignalRef.current) setStatus("idle");
    setCurrentAudio(null);
  }, [BACKEND_URL, sessionId, lang, muted, handleOfflineFallback]);

  const startRecording = async () => {
    stopAudioPlayback();
    setError("");
    setTranscript("");
    setReply("");
    setCaptionText("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;

      // ---- Start browser SpeechRecognition in parallel for LIVE transcript display ----
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SR) {
        try {
          const live = new SR();
          live.lang = STT_LOCALE[lang] || "en-IN";
          live.interimResults = true;
          live.continuous = true;
          live.maxAlternatives = 1;
          let finalSoFar = "";
          live.onresult = (e) => {
            let interim = "";
            for (let i = e.resultIndex; i < e.results.length; i++) {
              const r = e.results[i];
              if (r.isFinal) finalSoFar += r[0].transcript;
              else interim += r[0].transcript;
            }
            setTranscript((finalSoFar + " " + interim).trim());
          };
          live.onerror = () => { /* silent — Sarvam is source of truth */ };
          live.onend = () => { /* naturally stops when audio ends */ };
          liveRecogRef.current = live;
          try { live.start(); } catch { /* ignore */ }
        } catch { /* ignore */ }
      }

      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        streamRef.current?.getTracks().forEach((tk) => tk.stop());
        streamRef.current = null;
        if (liveRecogRef.current) {
          try { liveRecogRef.current.stop(); } catch { /* ignore */ }
          liveRecogRef.current = null;
        }
        if (vadRafRef.current) { cancelAnimationFrame(vadRafRef.current); vadRafRef.current = null; }
        if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch { /* ignore */ } audioCtxRef.current = null; }
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) { setStatus("idle"); return; }

        setStatus("transcribing");
        try {
          const fd = new FormData();
          const ext = (rec.mimeType || "audio/webm").includes("webm") ? "webm" : "wav";
          fd.append("file", blob, `voice.${ext}`);
          fd.append("language", lang);
          const res = await fetch(`${BACKEND_URL}/api/voice/stt`, { method: "POST", body: fd });
          if (!res.ok) throw new Error("stt failed");
          const data = await res.json();
          const text = (data.transcript || "").trim();
          if (!text) { setError("I couldn't catch that. Please try again."); setStatus("idle"); return; }
          setTranscript(text);  // replace interim with Sarvam's authoritative transcript
          await sendToBackend(text);
        } catch (e) {
          setError("Voice service unavailable. Please try again or type.");
          setStatus("idle");
        }
      };

      rec.start();
      setStatus("recording");

      // ---- Voice Activity Detection: auto-stop after ~1.5s of silence ----
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          audioCtxRef.current = ctx;
          const source = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 1024;
          source.connect(analyser);
          const buf = new Uint8Array(analyser.fftSize);
          const SILENCE_MS = 1500;    // 1.5s of quiet ends the turn
          const MIN_SPEECH_MS = 500;  // require ≥0.5s of audible speech first
          const THRESHOLD = 10;       // RMS threshold
          const MAX_MS = 30_000;      // hard 30s cap
          const startedAt = performance.now();
          let hasSpokenAt = 0;
          let silenceStart = 0;

          const tick = () => {
            const mr = mediaRecorderRef.current;
            if (!mr || mr.state !== "recording") return;
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) {
              const d = buf[i] - 128;
              sum += d * d;
            }
            const rms = Math.sqrt(sum / buf.length);
            const now = performance.now();

            if (rms > THRESHOLD) {
              if (!hasSpokenAt) hasSpokenAt = now;
              silenceStart = 0;
            } else if (hasSpokenAt && (now - hasSpokenAt) > MIN_SPEECH_MS) {
              if (!silenceStart) silenceStart = now;
              else if (now - silenceStart > SILENCE_MS) {
                try { mr.stop(); } catch { /* ignore */ }
                return;
              }
            }
            if (now - startedAt > MAX_MS) {
              try { mr.stop(); } catch { /* ignore */ }
              return;
            }
            vadRafRef.current = requestAnimationFrame(tick);
          };
          vadRafRef.current = requestAnimationFrame(tick);
        }
      } catch { /* VAD unavailable — user can tap to stop */ }
    } catch (err) {
      // Mic is optional — the type-below input works fine without permission.
      const denied = err && (err.name === "NotAllowedError" || err.name === "SecurityError");
      setMicBlocked(denied);
      setError(denied ? t("mic_optional_hint") : t("mic_start_failed"));
      setStatus("idle");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch { /* ignore */ }
    }
    if (liveRecogRef.current) {
      try { liveRecogRef.current.stop(); } catch { /* ignore */ }
    }
  };

  const onMicClick = () => {
    if (status === "recording") stopRecording();
    else if (status === "speaking") { stopAudioPlayback(); setStatus("idle"); }
    else if (status === "idle") startRecording();
  };

  const onSendTyped = () => {
    const txt = typedInput.trim();
    if (!txt || status === "thinking" || status === "transcribing") return;
    setTypedInput("");
    setTranscript(txt);
    sendToBackend(txt);
  };

  const askSuggested = (question) => {
    if (status === "thinking" || status === "transcribing" || status === "recording") return;
    stopAudioPlayback();
    setTranscript(question);
    sendToBackend(question);
  };

  useEffect(() => () => {
    stopAudioPlayback();
    stopRecording();
  }, [stopAudioPlayback]);

  // ---- Wake-word + Barge-in: always-on SpeechRecognition ----
  useEffect(() => {
    const refreshWake = () => { setWakeEnabled(getWakeWord()); setWakePhrase(getWakePhrase()); };
    window.addEventListener("sakhi:settings-updated", refreshWake);
    return () => window.removeEventListener("sakhi:settings-updated", refreshWake);
  }, []);

  // Barge-in hint: show the first BARGE_HINT_MAX times a girl hears Sakhi speak (when wake on)
  useEffect(() => {
    if (status === "speaking" && wakeEnabled && shouldShowBargeHint()) {
      setShowBargeHint(true);
      noteBargeHintShown();
    } else if (status !== "speaking") {
      setShowBargeHint(false);
    }
  }, [status, wakeEnabled]);

  useEffect(() => {
    if (!wakeEnabled) return;
    // Only listen when idle. During "speaking" the mic would pick up Sakhi Didi's
    // own voice via the speakers and self-trigger a barge-in, killing playback.
    if (status !== "idle") return;

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    let stopped = false;
    let restartTimer = null;
    const rec = new SR();
    rec.lang = STT_LOCALE[lang] || "en-IN";
    rec.interimResults = true;
    rec.continuous = true;
    rec.maxAlternatives = 1;

    const handleTrigger = () => {
      stopped = true;
      try { rec.stop(); } catch { /* ignore */ }
      setTimeout(() => { startRecording(); }, 120);
    };

    rec.onresult = (e) => {
      const tokens = getWakeTokens();
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const raw = e.results[i][0].transcript.toLowerCase();
        for (const tk of tokens) {
          if (raw.includes(tk)) { handleTrigger(); return; }
        }
      }
    };
    rec.onerror = () => { /* silent */ };
    rec.onend = () => {
      if (stopped) return;
      restartTimer = setTimeout(() => {
        try { rec.start(); } catch { /* ignore */ }
      }, 300);
    };

    wakeRecogRef.current = rec;
    try { rec.start(); } catch { /* ignore */ }

    return () => {
      stopped = true;
      if (restartTimer) clearTimeout(restartTimer);
      try { rec.stop(); } catch { /* ignore */ }
      wakeRecogRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wakeEnabled, status, lang]);

  const statusLabel =
    status === "recording" ? t("listening") :
    status === "transcribing" ? "…" :
    status === "thinking" ? "…" :
    status === "speaking" ? t("speaking") :
    t("tap_to_speak");

  const isActive = status === "recording" || status === "speaking";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {offline && (
        <div data-testid="offline-banner" className="mb-6 flex items-center gap-3 rounded-2xl bg-secondary/25 border border-secondary/40 px-5 py-3 text-sm font-semibold text-foreground">
          <WifiOff className="w-5 h-5 text-secondary-foreground" />
          {t("offline_banner")}
        </div>
      )}

      <div className="flex flex-col items-center text-center py-6" data-testid="voice-panel">
        {wakeEnabled && status === "idle" && (
          <div data-testid="wake-hint" className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/30 px-4 py-1.5 text-xs font-bold text-primary uppercase tracking-widest">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            {t("wake_word_active")} "{wakePhrase}"
          </div>
        )}
        <div className="relative">
          <span className={`absolute inset-0 rounded-full ${status === "recording" ? "bg-primary/30 animate-ping" : status === "speaking" ? "bg-accent/30 animate-ping" : "bg-transparent"}`} aria-hidden />
          <span className={`absolute -inset-6 rounded-full ${isActive ? "bg-primary/10 animate-pulse" : "bg-transparent"}`} aria-hidden />
          <button
            data-testid="mic-button"
            onClick={onMicClick}
            disabled={status === "transcribing" || status === "thinking"}
            aria-label={statusLabel}
            className={`relative w-40 h-40 sm:w-48 sm:h-48 rounded-full flex items-center justify-center shadow-[0_20px_60px_rgba(200,90,64,0.35)] transition-transform active:scale-95 focus:outline-none focus:ring-4 focus:ring-primary/40 disabled:opacity-70 ${
              status === "recording" ? "bg-primary text-white" :
              status === "speaking" ? "bg-accent text-white" :
              status === "transcribing" || status === "thinking" ? "bg-secondary text-foreground" :
              "bg-white text-primary border-4 border-primary"
            }`}
          >
            {status === "recording" ? <Square className="w-14 h-14 fill-white" strokeWidth={2.5} /> :
             status === "speaking" ? <Volume2 className="w-16 h-16" strokeWidth={2.5} /> :
             status === "transcribing" || status === "thinking" ? <Loader2 className="w-14 h-14 animate-spin" strokeWidth={2.5} /> :
             <Mic className="w-16 h-16" strokeWidth={2.5} />}
          </button>
        </div>

        <div className="mt-8" data-testid="status-label">
          <div className="text-sm uppercase tracking-widest text-muted-foreground font-bold">
            {status === "idle" ? t("chat_intro") : ""}
          </div>
          <div className="mt-1 text-2xl sm:text-3xl font-bold text-foreground">
            {statusLabel}
          </div>
        </div>

        <button
          data-testid="mute-toggle"
          onClick={() => { setMuted((m) => !m); if (!muted) stopAudioPlayback(); }}
          className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          {muted ? "Unmute Sakhi Didi's voice" : "Mute Sakhi Didi's voice"}
        </button>

        {showBargeHint && (
          <div
            data-testid="barge-hint"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent/10 border border-accent/30 px-4 py-1.5 text-xs font-semibold text-accent-foreground fade-up"
          >
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            {t("barge_hint")}
          </div>
        )}

        {error && (
          <div
            data-testid="chat-error"
            className={`mt-4 text-sm rounded-xl px-4 py-2 border ${
              micBlocked
                ? "text-foreground bg-secondary/20 border-secondary/40"
                : "text-destructive bg-destructive/10 border-destructive/30"
            }`}
          >
            {error}
          </div>
        )}
      </div>

      {/* Starter suggestion chips (empty state) */}
      {status === "idle" && !transcript && !reply && (
        <div className="mt-4" data-testid="starter-chips">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3 text-center">
            {t("suggested_questions")}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {["q_period_pain", "q_feel_sad", "q_what_is_period", "q_stay_safe"].map((k, idx) => (
              <button
                key={k}
                data-testid={`chip-${k}`}
                onClick={() => askSuggested(t(k))}
                className="rounded-full border-2 border-border bg-white px-4 py-2 text-sm font-semibold text-foreground hover:border-primary hover:text-primary transition-colors active:scale-95"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                {t(k)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Reading Mode caption */}
      {readingMode && captionText && (
        <ReadingCaption
          text={captionText}
          audioEl={currentAudio}
          active={status === "speaking"}
        />
      )}

      {(transcript || reply) && (
        <div className="mt-4 space-y-3">
          {transcript && (
            <div data-testid="user-transcript" className="ml-auto max-w-[85%] px-5 py-3 rounded-3xl rounded-tr-md bg-primary text-white text-base leading-relaxed whitespace-pre-wrap fade-up">
              {transcript}
              {status === "recording" && (
                <span className="inline-block ml-1 align-middle animate-pulse">▍</span>
              )}
            </div>
          )}
          {reply && !readingMode && (
            <div className="max-w-[85%] fade-up">
              <div data-testid="assistant-reply" className="px-5 py-3 rounded-3xl rounded-tl-md bg-white border border-border text-foreground text-base leading-relaxed whitespace-pre-wrap">
                {reply}
              </div>
              {status === "idle" && (
                <button
                  data-testid="reply-say-again"
                  onClick={() => speakTextOnce(reply)}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {t("say_again")}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {history.length > 2 && (
        <details className="mt-8 bg-white/60 border border-border rounded-2xl p-4">
          <summary className="cursor-pointer text-sm font-semibold text-muted-foreground">
            Previous conversation ({Math.floor(history.length / 2)})
          </summary>
          <div className="mt-4 space-y-3">
            {history.slice(0, -2).map((m, i) => (
              <div key={i} className={`text-sm px-4 py-2 rounded-2xl ${m.role === "user" ? "ml-auto bg-primary/10 text-foreground max-w-[85%]" : "bg-white border border-border text-foreground max-w-[85%]"}`}>
                {m.text}
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="mt-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">
          {t("or_type")}
        </div>
        <div className="flex items-center gap-3">
          <Input
            data-testid="chat-input"
            value={typedInput}
            onChange={(e) => setTypedInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSendTyped(); }}
            placeholder={t("type_message")}
            className="h-14 rounded-2xl border-2 text-base px-4 focus-visible:ring-primary bg-white"
            disabled={status === "thinking" || status === "transcribing"}
          />
          <Button
            data-testid="chat-send-btn"
            onClick={onSendTyped}
            disabled={status === "thinking" || status === "transcribing" || !typedInput.trim()}
            className="rounded-full w-14 h-14 flex-shrink-0 bg-primary hover:bg-primary/90 text-white shadow-lg disabled:opacity-40"
            aria-label={t("send")}
          >
            <Send className="w-6 h-6" strokeWidth={2.5} />
          </Button>
        </div>
      </div>
    </div>
  );
}
